import { Component, OnInit, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FileUpload } from 'primeng/fileupload';
import { CommunicationsService } from 'src/app/features/communications/_services/communications.service';
import {
  CommunicationAttachment,
  CommunicationChannel,
  CommunicationLog,
  CommunicationSendRequest,
  CommunicationTemplate,
  CommunicationTemplatePreviewResponse
} from 'src/app/features/communications/_models/communications.models';
import { NotificationService } from 'src/app/_share/_services/notification.service';
import { UserService } from 'src/app/_share/_services/user.service';
import { AuthorizationService } from 'src/app/_core/_services/authorization.service';
import { smsLength, SmsLength } from 'src/app/_share/_utils/sms-length';

// Mirrors the backend's attachment rules (support_and_communications
// services/communications.py) so a file is refused here with a clear
// message instead of after a full upload.
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const BLOCKED_ATTACHMENT_EXTENSIONS = new Set([
  '.exe', '.bat', '.cmd', '.com', '.scr', '.msi', '.pif', '.cpl',
  '.js', '.jse', '.vbs', '.vbe', '.wsf', '.ps1', '.jar', '.sh', '.dll'
]);

interface UploadingAttachment {
  id: number | null;
  original_filename: string;
  uploading: boolean;
}

interface TemplateVariableField {
  key: string;
  required: boolean;
  placeholder: string;
}

/**
 * Email layout variables the backend fills in itself when the key is absent
 * (communications._prepare_html_layout_variables). Supplying one - even
 * blank - overrides that default and, for subject_heading/message/data,
 * makes the send need send_freeform (send_permissions.RESERVED_LAYOUT_VARIABLES),
 * so blank ones are left out.
 */
const BACKEND_DEFAULTED_VARIABLES = new Set(['subject_heading', 'message', 'data', 'reset_link']);

@Component({
  selector: 'app-send-communication-dialog',
  templateUrl: './send-communication-dialog.component.html',
  styleUrls: ['./send-communication-dialog.component.scss']
})
export class SendCommunicationDialogComponent implements OnInit {
  @ViewChild(FileUpload) fileUpload?: FileUpload;

  channelOptions: { label: string; value: CommunicationChannel }[] = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' }
  ];
  channel: CommunicationChannel = 'email';

  portalTenants: { id: number; name: string }[] = [];
  portalTenantId: number | null = null;

  // Locked (pre-filled, read-only) when opened from Account Centre with a
  // known customer contact for the current channel; a free-text recipient
  // list otherwise (e.g. the Communications tab's page-level "Send").
  lockRecipient = false;
  recipientsInput = '';
  // The customer's contact details per channel, so switching Email <-> SMS
  // swaps the recipient instead of sending an email address to BulkSMS.
  private customerEmail = '';
  private customerMobile = '';

  templates: CommunicationTemplate[] = [];
  templatesLoading = false;
  selectedTemplateKey: string | null = null;

  // Templated send: the backend renders the template with these variables.
  variableFields: TemplateVariableField[] = [];
  variableValues: Record<string, string> = {};
  preview: CommunicationTemplatePreviewResponse | null = null;
  previewLoading = false;

  // A template's *rendered* content copied into the editable fields - sent
  // as freeform (needs communication_logs.send_freeform), never the raw
  // template with {{ placeholders }}.
  editingTemplate = false;

  subject = '';
  bodyHtml = '';
  bodyText = '';
  message = '';
  showHtmlPreview = false;

  attachments: UploadingAttachment[] = [];

  sending = false;

  canSendTemplated = false;
  canSendFreeform = false;

  private portalCustomerId: number | null = null;
  private subscriptionId: number | null = null;

  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private communicationsService: CommunicationsService,
    private userService: UserService,
    private authz: AuthorizationService,
    private ns: NotificationService
  ) {}

  ngOnInit(): void {
    const data = this.config.data || {};
    this.portalCustomerId = data.portalCustomerId ?? null;
    this.subscriptionId = data.subscriptionId ?? null;

    this.authz.ready$.subscribe(() => {
      this.canSendTemplated = this.authz.hasPermission('communication_logs.send_templated');
      this.canSendFreeform = this.authz.hasPermission('communication_logs.send_freeform');
    });

    const currentUser = this.userService.currentUser;
    this.portalTenants = currentUser?.portal_tenants || [];

    this.portalTenantId =
      data.portalTenantId ??
      (this.portalTenants.some((t) => t.id === currentUser?.portal_tenant_id)
        ? currentUser.portal_tenant_id
        : this.portalTenants[0]?.id) ??
      null;

    this.customerEmail = (data.recipientEmail || '').trim();
    this.customerMobile = (data.recipientMobile || '').trim();
    if (this.customerEmail || this.customerMobile) {
      this.lockRecipient = true;
      if (!this.customerEmail && this.customerMobile) this.channel = 'sms';
      this.applyCustomerRecipient();
    }

    this.loadTemplates();
  }

  get isEmail(): boolean {
    return this.channel === 'email';
  }

  get selectedTemplate(): CommunicationTemplate | undefined {
    return this.templates.find((t) => t.template_key === this.selectedTemplateKey);
  }

  /** Sending the template as-is, rendered server-side from variables. */
  get isTemplatedSend(): boolean {
    return !!this.selectedTemplateKey && !this.editingTemplate;
  }

  get recipients(): string[] {
    return this.recipientsInput
      .split(/[,;]/)
      .map((r) => r.trim())
      .filter((r) => !!r);
  }

  get sms(): SmsLength {
    return smsLength(this.message);
  }

  get smsOverLimit(): boolean {
    return this.sms.length > this.sms.limit;
  }

  get uploadsPending(): boolean {
    return this.attachments.some((a) => a.uploading);
  }

  get missingRequiredVariables(): string[] {
    return this.variableFields
      .filter((f) => f.required && !(this.variableValues[f.key] || '').trim())
      .map((f) => f.key);
  }

  get isValid(): boolean {
    if (!this.portalTenantId || this.recipients.length === 0 || this.uploadsPending) return false;

    if (this.isTemplatedSend) {
      return this.canSendTemplated && this.missingRequiredVariables.length === 0;
    }

    if (!this.canSendFreeform) return false;
    if (this.isEmail) {
      return !!this.subject && (!!this.bodyHtml || !!this.bodyText);
    }
    return !!this.message && !this.smsOverLimit;
  }

  // The recipient the customer has on file for the selected channel; empty
  // when they have none, in which case the field opens for manual entry.
  get customerRecipient(): string {
    return this.isEmail ? this.customerEmail : this.customerMobile;
  }

  get recipientLocked(): boolean {
    return this.lockRecipient && !!this.customerRecipient;
  }

  get missingCustomerRecipient(): boolean {
    return this.lockRecipient && !this.customerRecipient;
  }

  private applyCustomerRecipient(): void {
    if (this.lockRecipient) this.recipientsInput = this.customerRecipient;
  }

  onChannelChange(): void {
    this.clearTemplate();
    this.applyCustomerRecipient();
    this.loadTemplates();
  }

  onPortalTenantChange(): void {
    this.clearTemplate();
    this.loadTemplates();
  }

  loadTemplates(): void {
    if (!this.portalTenantId) {
      this.templates = [];
      return;
    }
    this.templatesLoading = true;
    this.communicationsService
      .getTemplates({
        channel: this.channel,
        portal_tenant_id: this.portalTenantId,
        active_only: true
      })
      .subscribe({
        next: (templates) => {
          this.templates = templates;
          this.templatesLoading = false;
        },
        error: () => {
          this.templates = [];
          this.templatesLoading = false;
        }
      });
  }

  private clearTemplate(): void {
    this.selectedTemplateKey = null;
    this.variableFields = [];
    this.variableValues = {};
    this.preview = null;
    this.editingTemplate = false;
  }

  onTemplateSelect(): void {
    const template = this.selectedTemplate;
    this.editingTemplate = false;
    this.preview = null;
    this.variableValues = {};
    this.variableFields = template ? this.buildVariableFields(template) : [];
  }

  /** One text input per scalar variable the template declares. */
  private buildVariableFields(template: CommunicationTemplate): TemplateVariableField[] {
    const definitions = (template.variables || {}) as Record<
      string,
      { type?: string; required?: boolean } | null
    >;
    const samples = (template.sample_variables || {}) as Record<string, unknown>;
    return Object.entries(definitions)
      .filter(([, def]) => !def?.type || def.type === 'string' || def.type === 'number')
      .map(([key, def]) => ({
        key,
        required: !!def?.required,
        placeholder:
          samples[key] !== undefined && typeof samples[key] !== 'object' ? String(samples[key]) : ''
      }));
  }

  /**
   * Every declared variable, blank ones as "" - the backend renders with
   * Jinja StrictUndefined, so an omitted optional variable (e.g.
   * `{% if portal_url %}`) fails with "'portal_url' is undefined".
   */
  private templateVariables(): Record<string, string> {
    const variables: Record<string, string> = {};
    for (const field of this.variableFields) {
      const value = this.variableValues[field.key] || '';
      if (!value.trim() && BACKEND_DEFAULTED_VARIABLES.has(field.key)) continue;
      variables[field.key] = value.trim() ? value : '';
    }
    return variables;
  }

  renderPreview(then?: (preview: CommunicationTemplatePreviewResponse) => void): void {
    const template = this.selectedTemplate;
    if (!template) return;

    this.previewLoading = true;
    this.communicationsService
      .previewTemplate(template.id, this.templateVariables(), this.portalTenantId ?? undefined)
      .subscribe({
        next: (preview) => {
          this.previewLoading = false;
          this.preview = preview;
          then?.(preview);
        },
        error: (err) => {
          this.previewLoading = false;
          this.ns.mainError(err?.error?.detail || 'Could not render the template.');
        }
      });
  }

  /** Copy the *rendered* template into the editable fields. */
  editTemplateContent(): void {
    this.renderPreview((preview) => {
      if (this.isEmail) {
        this.subject = preview.subject || '';
        this.bodyHtml = preview.body_html || '';
        this.bodyText = preview.body_text || '';
      } else {
        this.message = preview.body_text || '';
      }
      this.editingTemplate = true;
    });
  }

  onFileSelect(event: { files: File[] }): void {
    // PrimeNG (customUpload + auto) re-emits every file it has accumulated
    // on each selection - clear it so a second pick doesn't re-upload the
    // first file. Our own `attachments` list is the source of truth.
    this.fileUpload?.clear();

    for (const file of event.files) {
      const rejection = this.attachmentRejection(file);
      if (rejection) {
        this.ns.mainError(rejection);
        continue;
      }

      const placeholder: UploadingAttachment = {
        id: null,
        original_filename: file.name,
        uploading: true
      };
      this.attachments.push(placeholder);

      const formData = new FormData();
      formData.append('file', file);

      this.communicationsService.uploadAttachment(formData).subscribe({
        next: (attachment: CommunicationAttachment) => {
          placeholder.id = attachment.id;
          placeholder.uploading = false;
        },
        error: (err) => {
          this.ns.mainError(err?.error?.detail || `Failed to upload "${file.name}".`);
          this.attachments = this.attachments.filter((a) => a !== placeholder);
        }
      });
    }
  }

  private attachmentRejection(file: File): string | null {
    const dot = file.name.lastIndexOf('.');
    const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
    if (BLOCKED_ATTACHMENT_EXTENSIONS.has(extension)) {
      return `"${file.name}": ${extension} files can't be sent as attachments.`;
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      return `"${file.name}" is larger than the 10MB attachment limit.`;
    }
    return null;
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  send(): void {
    if (!this.isValid || this.sending) return;

    const attachmentIds = this.attachments.filter((a) => a.id !== null).map((a) => a.id as number);

    const base = {
      channel: this.channel,
      portal_tenant_id: this.portalTenantId as number,
      recipients: this.recipients,
      portal_customer_id: this.portalCustomerId,
      subscription_id: this.subscriptionId,
      attachment_ids: this.isEmail && attachmentIds.length ? attachmentIds : null
    };

    // Templated: template_key + variables only, so the backend renders it
    // and gates it on send_templated. Freeform/edited: content only, gated
    // on send_freeform (backend: helpers/send_permissions.is_freeform_send).
    const payload: CommunicationSendRequest = this.isTemplatedSend
      ? { ...base, template_key: this.selectedTemplateKey, variables: this.templateVariables() }
      : {
          ...base,
          template_key: null,
          ...(this.isEmail
            ? {
                subject: this.subject,
                body_html: this.bodyHtml || null,
                body_text: this.bodyText || null
              }
            : { message: this.message })
        };

    this.sending = true;
    this.communicationsService.sendCommunication(payload).subscribe({
      next: (log: CommunicationLog) => {
        this.sending = false;
        if (log?.status === 'blocked') {
          // Suppressed server-side (non-production, or this portal tenant
          // has communications disabled) - logged, not delivered.
          this.ns.mainWarning(
            'Not delivered: sending is blocked for this environment or portal tenant. It has been logged only.'
          );
        } else {
          this.ns.mainSuccess('Communication sent successfully.');
        }
        this.ref.close(log);
      },
      error: (err) => {
        this.sending = false;
        this.ns.mainError(err?.error?.detail || 'Failed to send communication.');
      }
    });
  }

  onCancel(): void {
    this.ref.close();
  }
}
